import type { AudioTrackOption, CaptionStyle, QualityLevel, SettingsPanel } from '../types'
import styles from './SettingsMenu.module.css'

const SPEEDS = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 1.75, 2]

interface Props {
  panel: SettingsPanel
  onPanel: (p: SettingsPanel) => void
  qualityLevels: QualityLevel[]
  activeQualityId: string
  qualityLabel: string
  playbackRate: number
  captionsOn: boolean
  hasCaptions: boolean
  captionStyle: CaptionStyle
  audioTracks: AudioTrackOption[]
  activeAudioId: string | null
  disabled?: boolean
  onQuality: (id: string) => void
  onSpeed: (rate: number) => void
  onCaptions: () => void
  onCaptionStyle: (s: CaptionStyle) => void
  onAudio: (id: string) => void
  sleepEnabled?: boolean
  sleepLabel?: string | null
  sleepRemainingSec?: number | null
  onSleep: (minutes: number | 'end' | null) => void
}

export function SettingsMenu({
  panel,
  onPanel,
  qualityLevels,
  activeQualityId,
  qualityLabel,
  playbackRate,
  captionsOn,
  hasCaptions,
  captionStyle,
  audioTracks,
  activeAudioId,
  disabled,
  onQuality,
  onSpeed,
  onCaptions,
  onCaptionStyle,
  onAudio,
  sleepEnabled = true,
  sleepLabel,
  sleepRemainingSec,
  onSleep,
}: Props) {
  if (disabled) return null

  return (
    <div className={styles.wrap}>
      <button
        type="button"
        className={styles.gear}
        aria-label="Settings"
        onClick={() => onPanel(panel ? null : 'root')}
      >
        <svg viewBox="0 0 24 24" width="24" height="24">
          <path
            fill="currentColor"
            d="M19.14 12.94c.04-.31.06-.63.06-.94s-.02-.63-.06-.94l2.03-1.58a.49.49 0 0 0 .12-.61l-1.92-3.32a.49.49 0 0 0-.59-.22l-2.39.96a7.2 7.2 0 0 0-1.62-.94l-.36-2.54A.49.49 0 0 0 13.9 2h-3.8a.49.49 0 0 0-.48.41l-.36 2.54c-.59.24-1.13.55-1.62.94l-2.39-.96a.49.49 0 0 0-.59.22L2.75 8.87a.49.49 0 0 0 .12.61l2.03 1.58c-.04.31-.06.63-.06.94s.02.63.06.94L2.87 14.5a.49.49 0 0 0-.12.61l1.92 3.32c.13.23.4.31.63.22l2.39-.96c.5.39 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.8c.24 0 .44-.17.48-.41l.36-2.54c.59-.24 1.13-.55 1.62-.94l2.39.96c.23.09.5 0 .63-.22l1.92-3.32a.49.49 0 0 0-.12-.61l-2.01-1.58zM12 15.6A3.6 3.6 0 1 1 12 8.4a3.6 3.6 0 0 1 0 7.2z"
          />
        </svg>
      </button>

      {panel && (
        <div className={styles.menu} role="menu">
          {panel === 'root' && (
            <>
              <button type="button" className={styles.item} onClick={() => onPanel('quality')}>
                <span>Quality</span>
                <span className={styles.value}>
                  {qualityLabel} <Chevron />
                </span>
              </button>
              <button type="button" className={styles.item} onClick={() => onPanel('speed')}>
                <span>Playback speed</span>
                <span className={styles.value}>
                  {playbackRate === 1 ? 'Normal' : `${playbackRate}x`} <Chevron />
                </span>
              </button>
              {audioTracks.length > 0 && (
                <button type="button" className={styles.item} onClick={() => onPanel('audio')}>
                  <span>Audio track</span>
                  <span className={styles.value}>
                    {audioTracks.find((a) => a.id === activeAudioId)?.label || 'Default'} <Chevron />
                  </span>
                </button>
              )}
              {hasCaptions && (
                <>
                  <button type="button" className={styles.item} onClick={onCaptions}>
                    <span>Subtitles/CC</span>
                    <span className={styles.value}>{captionsOn ? 'On' : 'Off'}</span>
                  </button>
                  <button type="button" className={styles.item} onClick={() => onPanel('captionStyle')}>
                    <span>Caption style</span>
                    <span className={styles.value}>
                      {captionStyle.fontSize} <Chevron />
                    </span>
                  </button>
                </>
              )}
              {sleepEnabled && (
                <button type="button" className={styles.item} onClick={() => onPanel('sleep')}>
                  <span>Sleep timer</span>
                  <span className={styles.value}>
                    {sleepLabel
                      ? sleepRemainingSec != null
                        ? `${Math.floor(sleepRemainingSec / 60)}:${String(sleepRemainingSec % 60).padStart(2, '0')}`
                        : sleepLabel
                      : 'Off'}{' '}
                    <Chevron />
                  </span>
                </button>
              )}
            </>
          )}

          {panel === 'quality' && (
            <>
              <button type="button" className={styles.back} onClick={() => onPanel('root')}>
                <Chevron left /> Quality
              </button>
              {qualityLevels.map((q) => (
                <button
                  key={q.id}
                  type="button"
                  className={`${styles.item} ${q.id === activeQualityId ? styles.active : ''}`}
                  onClick={() => {
                    onQuality(q.id)
                    onPanel(null)
                  }}
                >
                  {q.id === activeQualityId && <Check />}
                  <span>{q.label}</span>
                </button>
              ))}
            </>
          )}

          {panel === 'speed' && (
            <>
              <button type="button" className={styles.back} onClick={() => onPanel('root')}>
                <Chevron left /> Playback speed
              </button>
              {SPEEDS.map((s) => (
                <button
                  key={s}
                  type="button"
                  className={`${styles.item} ${s === playbackRate ? styles.active : ''}`}
                  onClick={() => {
                    onSpeed(s)
                    onPanel(null)
                  }}
                >
                  {s === playbackRate && <Check />}
                  <span>{s === 1 ? 'Normal' : `${s}`}</span>
                </button>
              ))}
            </>
          )}

          {panel === 'audio' && (
            <>
              <button type="button" className={styles.back} onClick={() => onPanel('root')}>
                <Chevron left /> Audio track
              </button>
              {audioTracks.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  className={`${styles.item} ${a.id === activeAudioId ? styles.active : ''}`}
                  onClick={() => {
                    onAudio(a.id)
                    onPanel(null)
                  }}
                >
                  {a.id === activeAudioId && <Check />}
                  <span>{a.label}</span>
                </button>
              ))}
            </>
          )}

          {panel === 'captionStyle' && (
            <>
              <button type="button" className={styles.back} onClick={() => onPanel('root')}>
                <Chevron left /> Caption style
              </button>
              {(['small', 'medium', 'large'] as const).map((size) => (
                <button
                  key={size}
                  type="button"
                  className={`${styles.item} ${captionStyle.fontSize === size ? styles.active : ''}`}
                  onClick={() => onCaptionStyle({ ...captionStyle, fontSize: size })}
                >
                  {captionStyle.fontSize === size && <Check />}
                  <span>Size: {size}</span>
                </button>
              ))}
              {[
                ['#ffffff', 'White'],
                ['#ffff00', 'Yellow'],
                ['#00ffff', 'Cyan'],
              ].map(([color, label]) => (
                <button
                  key={color}
                  type="button"
                  className={`${styles.item} ${captionStyle.color === color ? styles.active : ''}`}
                  onClick={() => onCaptionStyle({ ...captionStyle, color })}
                >
                  {captionStyle.color === color && <Check />}
                  <span>Color: {label}</span>
                </button>
              ))}
              {[0.4, 0.75, 1].map((op) => (
                <button
                  key={op}
                  type="button"
                  className={`${styles.item} ${captionStyle.backgroundOpacity === op ? styles.active : ''}`}
                  onClick={() => onCaptionStyle({ ...captionStyle, backgroundOpacity: op })}
                >
                  {captionStyle.backgroundOpacity === op && <Check />}
                  <span>Background: {Math.round(op * 100)}%</span>
                </button>
              ))}
            </>
          )}

          {panel === 'sleep' && (
            <>
              <button type="button" className={styles.back} onClick={() => onPanel('root')}>
                <Chevron left /> Sleep timer
              </button>
              <button
                type="button"
                className={`${styles.item} ${!sleepLabel ? styles.active : ''}`}
                onClick={() => {
                  onSleep(null)
                  onPanel(null)
                }}
              >
                {!sleepLabel && <Check />}
                <span>Off</span>
              </button>
              {([5, 10, 15, 30, 45] as const).map((m) => (
                <button
                  key={m}
                  type="button"
                  className={styles.item}
                  onClick={() => {
                    onSleep(m)
                    onPanel(null)
                  }}
                >
                  <span>{m} minutes</span>
                </button>
              ))}
              <button
                type="button"
                className={`${styles.item} ${sleepLabel === 'End of video' ? styles.active : ''}`}
                onClick={() => {
                  onSleep('end')
                  onPanel(null)
                }}
              >
                {sleepLabel === 'End of video' && <Check />}
                <span>End of video</span>
              </button>
            </>
          )}
        </div>
      )}
    </div>
  )
}

function Chevron({ left }: { left?: boolean }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="16"
      height="16"
      style={{ transform: left ? 'rotate(180deg)' : undefined }}
      aria-hidden
    >
      <path fill="currentColor" d="M9.29 6.71 13.58 11l-4.29 4.29L10.7 16.7l5.3-5.3-5.3-5.3z" />
    </svg>
  )
}

function Check() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" className={styles.check} aria-hidden>
      <path fill="currentColor" d="M9 16.17 4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
    </svg>
  )
}
